import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import SendIcon from "@material-ui/icons/Send";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import useWhatsappTemplates from "../../hooks/useWhatsappTemplates";
import useWhatsApps from "../../hooks/useWhatsApps";
import { openCloudPaymentRequiredDialog } from "../../utils/openCloudPaymentRequiredDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  areTemplateVariablesFilled,
  buildMetaTemplateComponents,
  buildTemplatePreviewParts,
  buildTemplatePreviewText,
  extractVariablesFromComponents,
  parseTemplateComponents,
} from "../../utils/templateVariables";

const useStyles = makeStyles((theme) => ({
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(5),
    position: "relative",
  },
  dialogHeader: {
    padding: theme.spacing(2.5, 3, 2),
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  titleTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  titleIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 193, 7, 0.12)",
    color: "#FFC107",
    flexShrink: 0,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.45,
    display: "block",
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  field: {
    marginBottom: 0,
  },
  previewSection: {
    marginTop: theme.spacing(0.5),
  },
  previewLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  phoneFrame: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "#0b141a",
  },
  phoneHeader: {
    background: "#1f2c34",
    color: "#e9edef",
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  phoneAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#54656f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
  },
  phoneBody: {
    minHeight: 180,
    padding: theme.spacing(2, 1.5, 2.5),
    backgroundColor: "#0b141a",
    backgroundImage:
      "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.03) 0, transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.03) 0, transparent 35%)",
  },
  todayPill: {
    margin: "0 auto 12px",
    width: "fit-content",
    padding: "4px 10px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.72rem",
  },
  messageBubble: {
    marginLeft: "auto",
    maxWidth: "88%",
    background: "#005c4b",
    color: "#e9edef",
    borderRadius: "12px 12px 2px 12px",
    padding: theme.spacing(1.25, 1.5),
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleHeader: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  bubbleFooter: {
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.65)",
  },
  bubbleButtons: {
    marginTop: theme.spacing(1),
    borderTop: "1px solid rgba(255,255,255,0.12)",
    paddingTop: theme.spacing(0.75),
  },
  bubbleButton: {
    textAlign: "center",
    color: "#53bdeb",
    fontSize: "0.82rem",
    padding: "4px 0",
  },
  bubbleMedia: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.75)",
  },
  bubbleTime: {
    marginTop: theme.spacing(0.75),
    textAlign: "right",
    fontSize: "0.68rem",
    color: "rgba(255,255,255,0.55)",
  },
  hint: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  manageLink: {
    marginTop: theme.spacing(1),
    display: "inline-block",
  },
  sendButtonGroup: {
    display: "flex",
    alignItems: "stretch",
    borderRadius: 4,
    overflow: "hidden",
  },
  sendPrimaryButton: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  scheduleButton: {
    minWidth: 44,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "1px solid rgba(255,255,255,0.25)",
  },
  schedulePanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
  },
}));

function formatPreviewTime(date = new Date()) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function WhatsappTemplatePickerModal({
  open,
  onClose,
  whatsappId,
  ticketId,
  contactId,
  onOptimisticMessage,
  senderName,
}) {
  const classes = useStyles();
  const { list } = useWhatsappTemplates();
  const { whatsApps } = useWhatsApps();
  const cloudPaymentRequired = useMemo(
    () =>
      Boolean(
        whatsApps.find(
          (line) => Number(line.id) === Number(whatsappId) && line.cloudPaymentRequired,
        ),
      ),
    [whatsApps, whatsappId],
  );
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [schedulePanelOpen, setSchedulePanelOpen] = useState(false);
  const [sendAt, setSendAt] = useState(moment().add(20, "minutes").format("YYYY-MM-DDTHH:mm"));
  const [selectedId, setSelectedId] = useState("");
  const [variableValues, setVariableValues] = useState({});

  const loadTemplates = useCallback(async () => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoading(true);
    try {
      const result = await list({
        whatsappId,
        status: "APPROVED",
        limit: 100,
      });
      setTemplates(result.templates ?? []);
    } catch (err) {
      toastError(err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [list, whatsappId]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSelectedId("");
      setVariableValues({});
      setSchedulePanelOpen(false);
      setSendAt(moment().add(20, "minutes").format("YYYY-MM-DDTHH:mm"));
    }
  }, [open, loadTemplates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedId)) ?? null,
    [templates, selectedId],
  );

  const variableIndexes = useMemo(
    () => extractVariablesFromComponents(selectedTemplate?.components ?? []),
    [selectedTemplate],
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setVariableValues({});
      return;
    }
    const nextValues = {};
    variableIndexes.forEach((index) => {
      nextValues[index] = "";
    });
    setVariableValues(nextValues);
  }, [selectedTemplate, variableIndexes]);

  const previewParts = useMemo(() => {
    if (!selectedTemplate) return [];
    return buildTemplatePreviewParts(selectedTemplate.components, variableValues);
  }, [selectedTemplate, variableValues]);

  const previewBody = useMemo(() => {
    if (!selectedTemplate) return "";
    return buildTemplatePreviewText(selectedTemplate.components, variableValues);
  }, [selectedTemplate, variableValues]);

  const canSend = Boolean(
    selectedTemplate &&
      areTemplateVariablesFilled(variableIndexes, variableValues) &&
      !sending &&
      !cloudPaymentRequired,
  );

  const notifyPaymentRequired = () => {
    openCloudPaymentRequiredDialog();
  };

  const handleVariableChange = (index, value) => {
    setVariableValues((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const buildTemplatePayload = () => {
    const body = previewBody || selectedTemplate.bodyPreview || selectedTemplate.name;
    const templateComponents = buildMetaTemplateComponents(
      selectedTemplate.components,
      variableValues,
    );
    return { body, templateComponents };
  };

  const handleSchedule = async () => {
    if (cloudPaymentRequired) {
      notifyPaymentRequired();
      return;
    }
    if (!ticketId || !contactId || !whatsappId || !selectedTemplate?.id || !canSend) return;

    const { body, templateComponents } = buildTemplatePayload();
    setScheduling(true);
    try {
      await api.post("/schedules", {
        body,
        sendAt: moment(sendAt).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        contactId,
        whatsappId,
        ticketId,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        ...(templateComponents ? { templateComponents } : {}),
        ...(Object.keys(variableValues).length > 0 ? { templateVariableValues: variableValues } : {}),
      });
      toast.success(i18n.t("whatsappTemplates.picker.scheduled"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setScheduling(false);
    }
  };

  const handleSend = async () => {
    if (cloudPaymentRequired) {
      notifyPaymentRequired();
      return;
    }
    if (!ticketId || !selectedTemplate?.id || !canSend) return;

    const tempId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const { body, templateComponents } = buildTemplatePayload();

    if (typeof onOptimisticMessage === "function") {
      try {
        onOptimisticMessage({
          id: tempId,
          ticketId,
          body,
          fromMe: true,
          mediaUrl: "",
          mediaType: null,
          quotedMsgId: null,
          isWhisper: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ack: 0,
          status: "pending",
          _optimistic: true,
          tempId,
        });
      } catch (e) {
        console.warn("onOptimisticMessage:", e);
      }
    }

    setSending(true);
    try {
      await api.post(`/messages/${ticketId}`, {
        fromMe: true,
        read: 1,
        body,
        templateId: selectedTemplate.id,
        ...(templateComponents ? { templateComponents } : {}),
        tempId,
      });
      toast.success(i18n.t("whatsappTemplates.picker.sent"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const renderPreviewBubble = () => {
    if (!selectedTemplate) {
      return (
        <Typography variant="body2" style={{ color: "rgba(255,255,255,0.55)" }}>
          {i18n.t("whatsappTemplates.picker.previewEmpty")}
        </Typography>
      );
    }

    const parsedComponents = parseTemplateComponents(selectedTemplate.components);
    const hasStructuredPreview = previewParts.some(
      (part) => part.kind !== "body" || part.text,
    );

    if (!hasStructuredPreview && selectedTemplate.bodyPreview) {
      return (
        <div className={classes.messageBubble}>
          <div>{selectedTemplate.bodyPreview}</div>
          <div className={classes.bubbleTime}>{formatPreviewTime()}</div>
        </div>
      );
    }

    return (
      <div className={classes.messageBubble}>
        {previewParts.map((part, index) => {
          if (part.kind === "header") {
            return (
              <div key={`header-${index}`} className={classes.bubbleHeader}>
                {part.text}
              </div>
            );
          }
          if (part.kind === "media") {
            return (
              <div key={`media-${index}`} className={classes.bubbleMedia}>
                [{part.label}]
              </div>
            );
          }
          if (part.kind === "media") {
            return (
              <div key={`media-${index}`} className={classes.bubbleMedia}>
                {part.previewUrl ? (
                  <img
                    src={part.previewUrl}
                    alt={part.label}
                    style={{ display: "block", width: "100%", maxHeight: 140, objectFit: "cover" }}
                  />
                ) : (
                  <>[{part.label}]</>
                )}
              </div>
            );
          }
          if (part.kind === "body") {
            return <div key={`body-${index}`}>{part.text}</div>;
          }
          if (part.kind === "footer") {
            return (
              <div key={`footer-${index}`} className={classes.bubbleFooter}>
                {part.text}
              </div>
            );
          }
          if (part.kind === "buttons") {
            return (
              <div key={`buttons-${index}`} className={classes.bubbleButtons}>
                {part.buttons.map((button, buttonIndex) => (
                  <div key={`btn-${buttonIndex}`} className={classes.bubbleButton}>
                    {button.text}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })}
        {!previewParts.length && parsedComponents.length === 0 && selectedTemplate.bodyPreview ? (
          <div>{selectedTemplate.bodyPreview}</div>
        ) : null}
        <div className={classes.bubbleTime}>{formatPreviewTime()}</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.titleRow}>
          <div className={classes.titleIcon}>
            <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
          </div>
          <div className={classes.titleTextBlock}>
            <Typography variant="h6" component="h2">
              {i18n.t("whatsappTemplates.picker.title")}
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              {i18n.t("whatsappTemplates.picker.subtitle")}
            </Typography>
          </div>
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: "absolute", right: 0, top: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress size={28} />
          </div>
        ) : templates.length === 0 ? (
          <div className={classes.empty}>
            <Typography variant="body2">
              {i18n.t("whatsappTemplates.picker.empty")}
            </Typography>
            <Link to="/whatsapp-templates" className={classes.manageLink} onClick={onClose}>
              {i18n.t("whatsappTemplates.picker.manageLink")}
            </Link>
          </div>
        ) : (
          <div className={classes.formSection}>
            {cloudPaymentRequired && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={notifyPaymentRequired}>
                    {i18n.t("whatsappTemplates.picker.paymentRequiredAction")}
                  </Button>
                }
              >
                {i18n.t("whatsappTemplates.picker.paymentRequiredWarning")}
              </Alert>
            )}
            <FormControl variant="outlined" fullWidth className={classes.field}>
              <InputLabel id="whatsapp-template-picker-label">
                {i18n.t("whatsappTemplates.picker.selectLabel")}
              </InputLabel>
              <Select
                labelId="whatsapp-template-picker-label"
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                label={i18n.t("whatsappTemplates.picker.selectLabel")}
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={String(template.id)}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {variableIndexes.map((index) => (
              <TextField
                key={`var-${index}`}
                className={classes.field}
                label={
                  selectedTemplate?.category === "AUTHENTICATION"
                    ? i18n.t("whatsappTemplates.dialog.authCodeLabel")
                    : i18n.t("whatsappTemplates.picker.variableLabel", { index })
                }
                value={variableValues[index] ?? ""}
                onChange={(event) => handleVariableChange(index, event.target.value)}
                variant="outlined"
                fullWidth
              />
            ))}

            <div className={classes.previewSection}>
              <div className={classes.previewLabel}>
                <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                {i18n.t("whatsappTemplates.picker.previewTitle")}
              </div>
              <div className={classes.phoneFrame}>
                <div className={classes.phoneHeader}>
                  <div className={classes.phoneAvatar}>W</div>
                  <span>{senderName || i18n.t("whatsappTemplates.picker.businessName")}</span>
                </div>
                <div className={classes.phoneBody}>
                  <div className={classes.todayPill}>
                    {i18n.t("whatsappTemplates.picker.today")}
                  </div>
                  {renderPreviewBubble()}
                </div>
              </div>
            </div>

            <Typography className={classes.hint}>
              {i18n.t("whatsappTemplates.picker.variablesHint")}
            </Typography>

            {schedulePanelOpen && (
              <div className={classes.schedulePanel}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t("whatsappTemplates.picker.scheduleTitle")}
                </Typography>
                <MuiPickersUtilsProvider utils={MomentUtils} locale="es">
                  <KeyboardDateTimePicker
                    variant="inline"
                    format="DD/MM/YYYY HH:mm"
                    margin="normal"
                    fullWidth
                    label={i18n.t("whatsappTemplates.picker.scheduleAt")}
                    value={sendAt}
                    onChange={(date) => {
                      if (date) {
                        setSendAt(moment(date).format("YYYY-MM-DDTHH:mm"));
                      }
                    }}
                    ampm={false}
                    minDate={moment()}
                  />
                </MuiPickersUtilsProvider>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  style={{ marginTop: 12 }}
                  disabled={!canSend || scheduling}
                  onClick={handleSchedule}
                  startIcon={
                    scheduling ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AccessTimeIcon />
                    )
                  }
                >
                  {i18n.t("whatsappTemplates.picker.schedule")}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending || scheduling}>
          {i18n.t("whatsappTemplates.buttons.cancel")}
        </Button>
        <div className={classes.sendButtonGroup}>
          <Button
            color="primary"
            variant="contained"
            className={classes.sendPrimaryButton}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!canSend || scheduling}
            onClick={handleSend}
          >
            {i18n.t("whatsappTemplates.picker.send")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.scheduleButton}
            disabled={!canSend || sending}
            onClick={() => setSchedulePanelOpen((prev) => !prev)}
            aria-label={i18n.t("whatsappTemplates.picker.schedule")}
          >
            <AccessTimeIcon fontSize="small" />
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default WhatsappTemplatePickerModal;
